import { create } from 'zustand';

/**
 * Client-side TTL cache for API responses, shared across all page mounts.
 *
 * TTL matches the backend Redis TTL (5 minutes) so navigating back to a page
 * that was recently loaded returns data instantly from this store instead of
 * re-firing the API call.
 *
 * Usage:
 *   const cache = useDataCache();
 *   const hit = cache.get('my-key');
 *   if (!hit) { ... fetch ... cache.set('my-key', data); }
 *
 * Keys should encode all filter dimensions so org-scoped data never bleeds
 * across different users or cities, e.g. `dashboard:${orgIds}:${orgType}:${city}`.
 *
 * Call cache.invalidate('dashboard') to evict all keys starting with 'dashboard'.
 * Call cache.clear() to wipe everything (e.g. on logout).
 */

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes — matches backend Redis TTL

export const useDataCache = create((set, get) => ({
  _cache: {},

  /** Return the cached value if it exists and has not expired, otherwise null. */
  get: (key) => {
    const entry = get()._cache[key];
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
    return entry.data;
  },

  /** Store a value under key. Overwrites any existing entry. */
  set: (key, data) =>
    set((state) => ({
      _cache: { ...state._cache, [key]: { data, ts: Date.now() } },
    })),

  /** Evict all keys whose name starts with prefix (e.g. 'dashboard', 'clinical'). */
  invalidate: (prefix) =>
    set((state) => ({
      _cache: Object.fromEntries(
        Object.entries(state._cache).filter(([k]) => !k.startsWith(prefix))
      ),
    })),

  /** Clear every cached entry — call on logout. */
  clear: () => set({ _cache: {} }),
}));
